import type { CombinedIdentity } from './api';

export interface IdentityRow {
  label: string;
  value: string | null;
  /** Shown as the "Submitter must complete" chip when `value` is not on file. */
  hint: string;
}

/** The complete EU / NI MDR dossier's identity rows — the reviewer checks these first (RA
 *  acceptance checklist, Class IIa: A.2–A.8, A.11). Rows that do not apply are left out: the
 *  representative's mandate / PRRC without a representative on file, the Art. 15(2) arrangement
 *  unless one is stated. LOCKSTEP with the PDF cover's `_identity_rows` in
 *  backend/services/dossier/eu_combined_renderer: same labels, same hints, same order. */
export function combinedIdentityRows(
  identity: CombinedIdentity | null | undefined,
  country: string,
): IdentityRow[] {
  const id = identity ?? null;
  const v = (key: Exclude<keyof CombinedIdentity, 'authorised_representative_applies'>): string | null => {
    const text = (id?.[key] ?? '').trim();
    return text || null;
  };
  const rows: IdentityRow[] = [
    {
      label: 'Basic UDI-DI',
      value: v('basic_udi_di'),
      hint: 'Basic UDI-DI, or the UDI assignment plan with its issuing entity (GS1, HIBCC, ICCBBA or IFA)',
    },
    {
      label: 'EUDAMED device registration',
      value: v('eudamed_registration'),
      hint: "State the device's EUDAMED registration status (UDI / Device module)",
    },
    {
      label: 'Manufacturer address',
      value: v('manufacturer_address'),
      hint: 'Registered place of business of the manufacturer',
    },
    {
      label: 'Manufacturer SRN',
      value: v('srn'),
      hint: 'SRN not yet issued: state the EUDAMED actor registration status',
    },
    {
      label:
        country.toUpperCase() === 'NI'
          ? 'EU/NI Authorised Representative (EC REP)'
          : 'EU Authorised Representative (EC REP)',
      value: v('authorised_representative'),
      hint: 'Authorised representative name, address and SRN (non-EU manufacturers, Article 11)',
    },
  ];
  // LOCKSTEP with _identity_rows: an EU manufacturer (applies === false) has no mandate / rep PRRC.
  if (v('authorised_representative') && id?.authorised_representative_applies !== false) {
    rows.push(
      {
        label: 'EC REP written mandate',
        value: v('ec_rep_mandate'),
        hint: 'Reference to the signed mandate appointing the authorised representative (Article 11)',
      },
      {
        label: 'EC REP PRRC (Article 15(6))',
        value: v('ec_rep_prrc'),
        hint: "The authorised representative's own PRRC",
      },
    );
  }
  rows.push(
    {
      label: 'PRRC (Article 15)',
      value: v('prrc'),
      hint: 'Person responsible for regulatory compliance',
    },
    {
      label: 'PRRC qualification evidence',
      value: v('prrc_qualification_evidence'),
      hint: "Reference to the PRRC's qualification evidence (Article 15(1))",
    },
  );
  if (v('prrc_arrangement')) {
    rows.push({ label: 'PRRC arrangement (Article 15(2))', value: v('prrc_arrangement'), hint: '' });
  }
  rows.push(
    {
      label: 'Notified Body',
      value: v('notified_body'),
      hint: 'Notified Body name and four-digit number',
    },
    {
      label: 'Applications to other notified bodies (Article 53(1))',
      value: v('other_nb_applications'),
      hint:
        'Manufacturer to state: no parallel application with another notified body; any earlier ' +
        'application refused or withdrawn',
    },
    {
      label: 'Conformity-assessment route',
      value: v('conformity_route'),
      hint: 'Article 52 conformity-assessment route',
    },
    {
      label: 'Consultation procedures (Annex IX Section 5)',
      value: v('consultation_procedures'),
      hint: 'Which Annex IX Section 5 consultations the device triggers',
    },
    {
      label: 'Device status',
      value: v('device_status'),
      hint: 'New CE marking or legacy device relying on Article 120, stated by the manufacturer',
    },
  );
  return rows;
}
